import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { formatMoney } from "@bw/shared/utils";
import { Fragment, useMemo, useRef, useState } from "react";
import { notifications } from "@/app/notifications";

import {
  movePricingRule,
  type MembershipPriceOverride,
  type PricingRule,
} from "@/libs/ratesApi";
import { MemberPricingRow, type OverrideState } from "./member-pricing-row";
import {
  PRICING_SERVICES,
  SERVICE_LABELS,
  type CourtOption,
  type PlanOption,
} from "./schema";
import {
  audienceSummary,
  cadenceSummary,
  chargeUnitSummary,
  effectiveSummary,
  recurrenceSummary,
  serviceSummary,
  targetSummary,
} from "./summarize-rule";
import { useVisibleClassOfferingLabels } from "./class-offering-picker";

// The number of columns in the rule table. The member-pricing expand row spans
// the same width.
const ROW_COLSPAN = 5;

const mobileCellSx = {
  display: { xs: "block", md: "table-cell" },
  minWidth: 0,
  p: { xs: 0, md: 1 },
  verticalAlign: "top",
  whiteSpace: "normal",
};

export interface RatesListProps {
  rules: PricingRule[];
  loading: boolean;
  error: string | null;
  facilityId: string;
  authorityGeneration: string;
  courts: CourtOption[];
  plans: PlanOption[];
  planNameById: Map<string, string>;
  // Re-read the authoritative order after every move attempt (server owns
  // precedence and may reject a stale snapshot).
  onReordered: () => void | Promise<void>;
  onEdit: (rule: PricingRule) => void;
  onDelete: (rule: PricingRule) => void;
  // Member-pricing expand wiring (lazy per-rule, owned by the workspace).
  overridesOpen: Record<string, boolean>;
  overrides: Record<string, OverrideState>;
  addRuleId: string | null;
  addPlanId: string;
  addPrice: string;
  addOverrideValid: boolean;
  savingOverride: boolean;
  onToggleOverrides: (rule: PricingRule) => void;
  onOpenAddOverride: (ruleId: string) => void;
  onCloseAddOverride: () => void;
  onPlanChange: (planId: string) => void;
  onPriceChange: (price: string) => void;
  onSubmitAddOverride: () => void;
  onDeleteOverride: (override: MembershipPriceOverride) => void;
}

// Service-grouped rule list. The quote engine filters by service before
// evaluating priority, so each group preserves its server order and exposes
// move controls only among siblings in that service. The caller's service-local
// sequence is the concurrency token; no unrelated service IDs are submitted.
export function RatesList({
  rules,
  loading,
  error,
  facilityId,
  authorityGeneration,
  courts,
  plans,
  planNameById,
  onReordered,
  onEdit,
  onDelete,
  overridesOpen,
  overrides,
  addRuleId,
  addPlanId,
  addPrice,
  addOverrideValid,
  savingOverride,
  onToggleOverrides,
  onOpenAddOverride,
  onCloseAddOverride,
  onPlanChange,
  onPriceChange,
  onSubmitAddOverride,
  onDeleteOverride,
}: RatesListProps) {
  // A reorder is in flight — disable the Move controls so a double-click can't
  // fire two competing reorders.
  const bindingToken = JSON.stringify([facilityId, authorityGeneration]);
  const activation = useRef({ bindingToken, epoch: 0 });
  if (activation.current.bindingToken !== bindingToken) {
    activation.current = { bindingToken, epoch: activation.current.epoch + 1 };
  }
  const scopeToken = JSON.stringify([bindingToken, activation.current.epoch]);
  const activeScope = useRef(scopeToken);
  activeScope.current = scopeToken;
  const [reorderRecord, setReordering] = useState({ scopeToken, busy: false });
  const reordering =
    reorderRecord.scopeToken === scopeToken && reorderRecord.busy;
  const [navigation, setNavigation] = useState({ scopeToken, page: 0 });
  const page = navigation.scopeToken === scopeToken ? navigation.page : 0;
  const visibleRules = rules.slice(page * 25, (page + 1) * 25);
  const visibleIds = new Set(visibleRules.map((rule) => rule.rule_id));
  const labels = useVisibleClassOfferingLabels(
    facilityId,
    authorityGeneration,
    visibleRules
      .filter((rule) => rule.service === "classes" && rule.offering_id)
      .map((rule) => rule.offering_id as string),
    !loading && !error,
  );

  // Name lookup so the summary renders court NAMES, never raw UUIDs.
  const courtNameById = useMemo(() => {
    const names = new Map<string, string>();
    courts.forEach((court) => names.set(court.id, court.name));
    return names;
  }, [courts]);

  const offeringNameById = useMemo(() => {
    const names = new Map<string, string>();
    for (const [id, label] of Object.entries(labels.data ?? {})) {
      if (label) names.set(id, `${label.program_name} — ${label.track_name}`);
    }
    return names;
  }, [labels.data]);

  const groups = useMemo(
    () =>
      PRICING_SERVICES.map((service) => ({
        service,
        rules: rules.filter((rule) => rule.service === service),
      })).filter((group) => group.rules.length > 0),
    [rules],
  );

  const move = async (rule: PricingRule, direction: -1 | 1) => {
    const capturedScope = scopeToken;
    if (activeScope.current !== capturedScope) return;
    const serviceRules = rules.filter(
      (candidate) => candidate.service === rule.service,
    );
    const serviceIndex = serviceRules.findIndex(
      (candidate) => candidate.rule_id === rule.rule_id,
    );
    const targetServiceIndex = serviceIndex + direction;
    if (
      serviceIndex < 0 ||
      targetServiceIndex < 0 ||
      targetServiceIndex >= serviceRules.length
    ) {
      return;
    }
    if (reordering) return;

    setReordering({ scopeToken: capturedScope, busy: true });
    try {
      await movePricingRule(
        facilityId,
        rule.rule_id,
        direction < 0 ? "up" : "down",
        serviceRules.map((candidate) => candidate.rule_id),
      );
      if (activeScope.current !== capturedScope) return;
      await onReordered();
    } catch (caughtError) {
      if (activeScope.current !== capturedScope) return;
      notifications.error(
        caughtError instanceof Error
          ? caughtError.message
          : "Could not move rate.",
      );
      try {
        await onReordered();
      } catch {
        // The workspace's normal read-error path owns refetch failures.
      }
    } finally {
      if (activeScope.current === capturedScope)
        setReordering({ scopeToken: capturedScope, busy: false });
    }
  };

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (loading) {
    return (
      <Stack
        aria-label="Loading rates"
        role="status"
        spacing={1}
        sx={{ alignItems: "center", py: 5 }}
      >
        <CircularProgress aria-hidden size={24} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    );
  }

  if (rules.length === 0) {
    return (
      <Typography
        color="text.secondary"
        sx={{ py: 5, textAlign: "center" }}
        variant="body2"
      >
        No rates yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {labels.isError ? (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={labels.restart}>
              Retry class labels
            </Button>
          }
        >
          Class option labels unavailable right now.
        </Alert>
      ) : null}
      <TableContainer sx={{ maxWidth: "100%" }}>
        <Table
          aria-label="Facility rates"
          role="table"
          sx={{ display: { xs: "block", md: "table" }, width: "100%" }}
        >
          <TableHead sx={{ display: { xs: "none", md: "table-header-group" } }}>
            <TableRow role="row">
              <TableCell role="columnheader">Rate</TableCell>
              <TableCell role="columnheader">Service and target</TableCell>
              <TableCell role="columnheader">Schedule and audience</TableCell>
              <TableCell role="columnheader">Price</TableCell>
              <TableCell aria-label="Actions" role="columnheader" />
            </TableRow>
          </TableHead>
          <TableBody sx={{ display: { xs: "block", md: "table-row-group" } }}>
            {groups
              .filter((group) =>
                group.rules.some((rule) => visibleIds.has(rule.rule_id)),
              )
              .map((group) => (
                <Fragment key={group.service}>
                  <TableRow
                    role="row"
                    sx={{
                      bgcolor: "action.hover",
                      display: { xs: "block", md: "table-row" },
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <TableCell
                      colSpan={ROW_COLSPAN}
                      role="cell"
                      sx={{
                        display: { xs: "block", md: "table-cell" },
                        py: 1.25,
                        whiteSpace: "normal",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 0.25, sm: 1 }}
                        sx={{
                          alignItems: { xs: "flex-start", sm: "baseline" },
                        }}
                      >
                        <Typography sx={{ fontWeight: 700 }} variant="body2">
                          {SERVICE_LABELS[group.service]} rates
                        </Typography>
                        <Typography color="text.secondary" variant="caption">
                          Priority applies within this service.
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {group.rules
                    .filter((rule) => visibleIds.has(rule.rule_id))
                    .map((rule) => {
                      const serviceIndex = group.rules.findIndex(
                        (candidate) => candidate.rule_id === rule.rule_id,
                      );
                      // The row title is the operator's name if set, else the cadence.
                      const hasName = Boolean(rule.name?.trim());
                      const title = hasName ? rule.name : cadenceSummary(rule);
                      const service = serviceSummary(rule);
                      const target =
                        rule.service === "classes" &&
                        !offeringNameById.has(rule.offering_id ?? "")
                          ? labels.ready || !rule.offering_id
                            ? "Class option unavailable"
                            : labels.isError
                              ? "Class option unavailable right now."
                              : "Loading class option…"
                          : targetSummary(
                              rule,
                              courtNameById,
                              offeringNameById,
                            );
                      const audience = audienceSummary(rule, planNameById);
                      const cadence = cadenceSummary(rule);
                      const recurrence = recurrenceSummary(rule);
                      const effective = effectiveSummary(rule);
                      const chargeUnit = chargeUnitSummary(rule);
                      const entryFee =
                        rule.entry_fee != null
                          ? formatMoney(rule.entry_fee, rule.currency)
                          : null;
                      const extraFee =
                        rule.extra_player_fee != null
                          ? formatMoney(rule.extra_player_fee, rule.currency)
                          : null;
                      const isFirst = serviceIndex === 0;
                      const isLast = serviceIndex === group.rules.length - 1;
                      const inactive = rule.eligibility_status !== "active";

                      return (
                        <Fragment key={rule.rule_id}>
                          <TableRow
                            role="row"
                            sx={{
                              display: {
                                xs: "grid",
                                md: "table-row",
                              },
                              gap: { xs: 1, md: 0 },
                              gridTemplateColumns: "minmax(0, 1fr) auto",
                              px: { xs: 1, md: 0 },
                              py: { xs: 1.5, md: 0 },
                            }}
                          >
                            <TableCell
                              role="cell"
                              sx={{
                                ...mobileCellSx,
                                gridColumn: { xs: "1", md: "auto" },
                                gridRow: { xs: "1", md: "auto" },
                                minWidth: { md: 160 },
                              }}
                            >
                              <Stack
                                spacing={0.25}
                                sx={{ alignItems: "flex-start" }}
                              >
                                <Typography
                                  sx={{ fontWeight: 700 }}
                                  variant="body2"
                                >
                                  {title}
                                </Typography>
                                <Typography
                                  color="text.secondary"
                                  variant="caption"
                                >
                                  {rule.source === "simple"
                                    ? "Simple"
                                    : "Advanced"}
                                </Typography>
                                {inactive ? (
                                  <Chip
                                    color="error"
                                    label="Inactive"
                                    size="small"
                                  />
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell
                              role="cell"
                              sx={{
                                ...mobileCellSx,
                                gridColumn: { xs: "1 / -1", md: "auto" },
                                gridRow: { xs: "2", md: "auto" },
                                minWidth: { md: 192 },
                              }}
                            >
                              <Stack spacing={0.25}>
                                <Typography variant="body2">
                                  {service}
                                </Typography>
                                <Typography
                                  color="text.secondary"
                                  variant="caption"
                                >
                                  {target}
                                </Typography>
                                {rule.eligibility_reason ? (
                                  <Typography color="error" variant="caption">
                                    {rule.eligibility_reason}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell
                              role="cell"
                              sx={{
                                ...mobileCellSx,
                                gridColumn: { xs: "1 / -1", md: "auto" },
                                gridRow: { xs: "3", md: "auto" },
                                minWidth: { md: 176 },
                              }}
                            >
                              <Stack spacing={0.25}>
                                <Typography variant="body2">
                                  {!rule.all_day &&
                                  rule.start_time &&
                                  rule.end_time ? (
                                    <>
                                      {recurrence ? (
                                        <>
                                          <span>{recurrence}</span>
                                          <span> · </span>
                                        </>
                                      ) : null}
                                      <span>{rule.start_time}</span>
                                      <span>–</span>
                                      <span>{rule.end_time}</span>
                                    </>
                                  ) : (
                                    cadence
                                  )}
                                </Typography>
                                <Typography
                                  color="text.secondary"
                                  variant="caption"
                                >
                                  <span>{audience}</span>
                                  <span> · </span>
                                  <span>{effective}</span>
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell
                              align="right"
                              role="cell"
                              sx={{
                                ...mobileCellSx,
                                gridColumn: { xs: "2", md: "auto" },
                                gridRow: { xs: "1", md: "auto" },
                                textAlign: { xs: "right", md: "left" },
                              }}
                            >
                              <Stack
                                spacing={0.25}
                                sx={{
                                  alignItems: {
                                    xs: "flex-end",
                                    md: "flex-start",
                                  },
                                }}
                              >
                                <Typography
                                  sx={{ fontWeight: 700 }}
                                  variant="body2"
                                >
                                  {formatMoney(rule.base_price, rule.currency)}
                                </Typography>
                                <Typography
                                  color="text.secondary"
                                  variant="caption"
                                >
                                  {chargeUnit}
                                </Typography>
                                {entryFee ? (
                                  <Typography
                                    color="text.secondary"
                                    variant="caption"
                                  >
                                    Entry <span>{entryFee}</span>
                                  </Typography>
                                ) : null}
                                {extraFee ? (
                                  <Typography
                                    color="text.secondary"
                                    variant="caption"
                                  >
                                    Extra player <span>{extraFee}</span>
                                  </Typography>
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell
                              align="right"
                              role="cell"
                              sx={{
                                ...mobileCellSx,
                                gridColumn: { xs: "1 / -1", md: "auto" },
                                gridRow: { xs: "4", md: "auto" },
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={0.25}
                                sx={{ justifyContent: "flex-end" }}
                              >
                                <IconButton
                                  aria-label="Move up"
                                  disabled={inactive || isFirst || reordering}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void move(rule, -1);
                                  }}
                                  size="small"
                                  title="Move up"
                                >
                                  <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  aria-label="Move down"
                                  disabled={inactive || isLast || reordering}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void move(rule, 1);
                                  }}
                                  size="small"
                                  title="Move down"
                                >
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                                {rule.supports_membership_price_overrides ? (
                                  <IconButton
                                    aria-expanded={
                                      !!overridesOpen[rule.rule_id]
                                    }
                                    aria-label="Member pricing"
                                    onClick={() => onToggleOverrides(rule)}
                                    size="small"
                                    title="Member pricing"
                                  >
                                    <PeopleAltOutlinedIcon fontSize="small" />
                                  </IconButton>
                                ) : null}
                                <IconButton
                                  aria-label="Edit rate"
                                  onClick={() => onEdit(rule)}
                                  size="small"
                                  title="Edit"
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  aria-label="Delete rate"
                                  color="error"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onDelete(rule);
                                  }}
                                  size="small"
                                  title="Delete"
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                          {rule.supports_membership_price_overrides &&
                          overridesOpen[rule.rule_id] ? (
                            <MemberPricingRow
                              addOpen={addRuleId === rule.rule_id}
                              addOverrideValid={addOverrideValid}
                              addPlanId={addPlanId}
                              addPrice={addPrice}
                              colSpan={ROW_COLSPAN}
                              onCloseAdd={onCloseAddOverride}
                              onDelete={onDeleteOverride}
                              onOpenAdd={() => onOpenAddOverride(rule.rule_id)}
                              onPlanChange={onPlanChange}
                              onPriceChange={onPriceChange}
                              onSubmitAdd={onSubmitAddOverride}
                              planNameById={planNameById}
                              plans={plans}
                              rule={rule}
                              savingOverride={savingOverride}
                              state={overrides[rule.rule_id]}
                            />
                          ) : null}
                        </Fragment>
                      );
                    })}
                </Fragment>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          disabled={reordering || page === 0}
          onClick={() => setNavigation({ scopeToken, page: page - 1 })}
        >
          Previous rates
        </Button>
        <Button
          size="small"
          disabled={reordering || (page + 1) * 25 >= rules.length}
          onClick={() => setNavigation({ scopeToken, page: page + 1 })}
        >
          Next rates
        </Button>
      </Stack>
    </Stack>
  );
}
